import axios from 'axios';

const BASE_URL = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${BASE_URL}/api`;

const apiClient = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000,
});

// Attach JWT on every request + tell the backend our public URL
// (used by signing.py to build correct signing links in any environment)
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('c4p_access_token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    config.headers['X-App-Url'] = BASE_URL;
    return config;
  },
  (error) => Promise.reject(error)
);

// Auto-refresh on 401 (skip for auth endpoints to allow error propagation)
apiClient.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config;
    // Don't intercept auth endpoints (login, reset-password) - let them propagate errors
    const isAuthEndpoint = original.url?.includes('/auth/login') || original.url?.includes('/auth/reset-password');
    if (error.response?.status === 401 && !original._retry && !isAuthEndpoint) {
      original._retry = true;
      const refresh = localStorage.getItem('c4p_refresh_token');
      if (refresh) {
        try {
          const res = await axios.post(`${API_URL}/auth/refresh`, { refresh_token: refresh });
          localStorage.setItem('c4p_access_token', res.data.access_token);
          localStorage.setItem('c4p_refresh_token', res.data.refresh_token);
          original.headers.Authorization = `Bearer ${res.data.access_token}`;
          return apiClient(original);
        } catch {
          localStorage.removeItem('c4p_access_token');
          localStorage.removeItem('c4p_refresh_token');
          window.location.href = '/login';
        }
      } else {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export default apiClient;

export const authAPI = {
  login: (email, password) => apiClient.post('/auth/login', { email, password }),
  logout: () => apiClient.post('/auth/logout'),
  me: () => apiClient.get('/auth/me'),
  updateProfile: (data) => apiClient.patch('/auth/me/profile', data),
  refresh: (rt) => apiClient.post('/auth/refresh', { refresh_token: rt }),
  resetPassword: (data) => apiClient.post('/auth/reset-password', data),
};

export const usersAPI = {
  list: (params) => apiClient.get('/users/', { params }),
  create: (data) => apiClient.post('/users/', data),
  update: (id, data) => apiClient.put(`/users/${id}`, data),
  deactivate: (id) => apiClient.delete(`/users/${id}`),
  getPartners: (id) => apiClient.get(`/users/${id}/partners`),
  setPassword: (id, newPassword) => apiClient.post(`/users/${id}/set-password`, { new_password: newPassword }),
  directory: (role) => apiClient.get('/users/directory', { params: role ? { role } : {} }),
};

export const customersAPI = {
  list: (params) => apiClient.get('/customers/', { params }),
  get: (id) => apiClient.get(`/customers/${id}`),
  create: (data) => apiClient.post('/customers/', data),
  update: (id, data) => apiClient.put(`/customers/${id}`, data),
  delete: (id) => apiClient.delete(`/customers/${id}`),
  addContact: (id, data) => apiClient.post(`/customers/${id}/contacts`, data),
  updateContact: (id, cid, data) => apiClient.put(`/customers/${id}/contacts/${cid}`, data),
  deleteContact: (id, cid) => apiClient.delete(`/customers/${id}/contacts/${cid}`),
  updateMsa: (id, data) => apiClient.patch(`/customers/${id}/msa`, data),
  uploadMsaDoc: (id, formData) => apiClient.post(`/customers/${id}/msa/document`, formData),
  getMsaDoc: (id) => apiClient.get(`/customers/${id}/msa/document`, { responseType: 'blob' }),
};

export const tenantsAPI = {
  list: (params) => apiClient.get('/tenants/', { params }),
  get: (id) => apiClient.get(`/tenants/${id}`),
  create: (data) => apiClient.post('/tenants/', data),
  update: (id, data) => apiClient.put(`/tenants/${id}`, data),
  patch: (id, data) => apiClient.patch(`/tenants/${id}`, data),
  delete: (id) => apiClient.delete(`/tenants/${id}`),
  addContact: (id, data) => apiClient.post(`/tenants/${id}/contacts`, data),
  updateContact: (id, cid, data) => apiClient.put(`/tenants/${id}/contacts/${cid}`, data),
  deleteContact: (id, cid) => apiClient.delete(`/tenants/${id}/contacts/${cid}`),
  updateMsa: (id, data) => apiClient.patch(`/tenants/${id}/msa`, data),
  uploadMsaDoc: (id, formData) => apiClient.post(`/tenants/${id}/msa/document`, formData),
  getMsaDoc: (id) => apiClient.get(`/tenants/${id}/msa/document`, { responseType: 'blob' }),
  setBillingViaPartner: (id, billingViaPartner) => apiClient.patch(`/tenants/${id}/billing-type`, { billing_via_partner: billingViaPartner }),
};

export const resourcesAPI = {
  list: (params) => apiClient.get('/resources/', { params }),
  get: (id) => apiClient.get(`/resources/${id}`),
  create: (data) => apiClient.post('/resources/', data),
  update: (id, data) => apiClient.put(`/resources/${id}`, data),
  setStatus: (id, status) => apiClient.patch(`/resources/${id}/status`, { status }),
  remove: (id) => apiClient.delete(`/resources/${id}`),
  getFeaturedVisibility: () => apiClient.get('/admin/featured-resources-visibility'),
  setFeaturedVisibility: (data) => apiClient.put('/admin/featured-resources-visibility', data),
};

export const mtproAPI = {
  listSystems: (params) => apiClient.get('/mtpro/systems', { params }),
  getSystem: (id) => apiClient.get(`/mtpro/systems/${id}`),
  updateSystem: (id, data) => apiClient.patch(`/mtpro/systems/${id}`, data),
  linkTenant: (systemId, data) => apiClient.post(`/mtpro/systems/${systemId}/tenants`, data),
  unlinkTenant: (systemId, tenantId) => apiClient.delete(`/mtpro/systems/${systemId}/tenants/${tenantId}`),
  updateTenant: (systemId, tenantId, data) => apiClient.patch(`/mtpro/systems/${systemId}/tenants/${tenantId}`, data),
};

export const quotesAPI = {
  list: (params) => apiClient.get('/quotes/', { params }),
  get: (id) => apiClient.get(`/quotes/${id}`),
  create: (data) => apiClient.post('/quotes/', data),
  update: (id, data) => apiClient.put(`/quotes/${id}`, data),
  updateStatus: (id, status) => apiClient.patch(`/quotes/${id}/status`, { status }),
  saveAsNew: (id) => apiClient.post(`/quotes/${id}/save-as-new`),
  saveAsVariant: (id) => apiClient.post(`/quotes/${id}/save-as-variant`),
  getVariants: (id) => apiClient.get(`/quotes/${id}/variants`),
  setVariantPrimary: (id) => apiClient.post(`/quotes/${id}/set-variant-primary`),
  duplicate: (id, customerId) => apiClient.post(`/quotes/${id}/duplicate`, { customer_id: customerId }),
  delete: (id) => apiClient.delete(`/quotes/${id}`),
  getVersions: (id) => apiClient.get(`/quotes/${id}/versions`),
  getDealRefs: (customerId, q) => apiClient.get('/quotes/deal-refs', { params: { customer_id: customerId, ...(q ? { q } : {}) } }),
  setDealPrimary: (id) => apiClient.post(`/quotes/${id}/set-deal-primary`),
  reassignPsm: (id, data) => apiClient.patch(`/quotes/${id}/reassign-psm`, data),
};

export const pricingAPI = {
  calculate: (data) => apiClient.post('/pricing/calculate', data),
  getConfig: () => apiClient.get('/pricing/config'),   // full config with server_catalog
  getPricingConfig: () => apiClient.get('/pricing/config'),  // alias for guide
  getExtras: () => apiClient.get('/admin/extras'),
  getDbTiers: () => apiClient.get('/admin/db-upgrade-tiers'),
};

export const aiAPI = {
  parseQuote: (text) => apiClient.post('/ai/parse-quote', { text }),
  summariseQuote: (quote_id) => apiClient.post('/ai/summarise-quote', { quote_id }),
  recommendExtras: (data) => apiClient.post('/ai/recommend-extras', data),
};

export const contractsAPI = {
  list: (params) => apiClient.get('/contracts/', { params }),
  get: (id) => apiClient.get(`/contracts/${id}`),
  rename: (id, contract_name) => apiClient.patch(`/contracts/${id}`, { contract_name }),
  createChangeRequest: (id) => apiClient.post(`/contracts/${id}/create-change-request`),
  archive: (id) => apiClient.delete(`/contracts/${id}/archive`),
  terminate: (id, reason) => apiClient.post(`/contracts/${id}/terminate`, { reason: reason || null }),
  convertManualQuote: (quoteId, contractStartDate) => apiClient.post(`/contracts/manual-quote/${quoteId}/convert`, { contract_start_date: contractStartDate || null }),
  // Backward compat alias for old /orders/ routes (for any cached bookmarks)
  listLegacy: (params) => apiClient.get('/contracts/', { params }),
};

export const adminAPI = {
  // Pricing
  getStandardPricing: () => apiClient.get('/admin/pricing/standard'),
  updateStandardPricing: (data) => apiClient.put('/admin/pricing/standard', data),
  getStandalonePupm: () => apiClient.get('/admin/pricing/standalone-pupm'),
  updateStandalonePupm: (data) => apiClient.put('/admin/pricing/standalone-pupm', data),
  getAdvancedTiers: () => apiClient.get('/admin/pricing/advanced/tiers'),
  updateAdvancedTier: (id, data) => apiClient.put(`/admin/pricing/advanced/tiers/${id}`, data),
  createAdvancedTier: (data) => apiClient.post('/admin/pricing/advanced/tiers', data),
  deleteAdvancedTier: (id) => apiClient.delete(`/admin/pricing/advanced/tiers/${id}`),
  getDbUpgradeTiers: () => apiClient.get('/admin/pricing/db-upgrade-tiers'),
  updateDbUpgradeTier: (id, data) => apiClient.put(`/admin/pricing/db-upgrade-tiers/${id}`, data),
  // Extras
  getExtras: () => apiClient.get('/admin/extras'),
  createExtra: (data) => apiClient.post('/admin/extras', data),
  updateExtra: (id, data) => apiClient.put(`/admin/extras/${id}`, data),
  deleteExtra: (id) => apiClient.delete(`/admin/extras/${id}`),
  // Currencies
  getCurrencies: () => apiClient.get('/admin/currencies'),
  updateCurrency: (code, data) => apiClient.put(`/admin/currencies/${code}`, data),
  // Users
  getUsers: () => apiClient.get('/users/'),
  createUser: (data) => apiClient.post('/users/', data),
  updateUser: (id, data) => apiClient.put(`/users/${id}`, data),
  // Settings
  getSettings: () => apiClient.get('/admin/settings'),
  updateSetting: (key, value) => apiClient.put(`/admin/settings/${key}`, { value }),
  // Server catalog
  getServerCatalog: (params) => apiClient.get('/admin/server-catalog', { params }),
  // Server catalog - code in body (avoids nginx %2F path decoding issue)
  updateServerDetails: (id, data) => apiClient.put(`/admin/server-catalog/${id}`, data),
  updateServerPrices: (code, data) => apiClient.post('/admin/server-catalog/update-prices', { code, ...data }),
  createServer: (data) => apiClient.post('/admin/server-catalog', data),
  toggleServerActive: (code) => apiClient.post('/admin/server-catalog/toggle-active', { code }),
  deleteServer: (code) => apiClient.post('/admin/server-catalog/delete', { code }),
  // Storage pricing
  getStoragePricing: () => apiClient.get('/admin/storage-pricing'),
  updateStoragePricing: (type, data) => apiClient.put(`/admin/storage-pricing/${type}`, data),
  // Audit
  getAuditLog: (params) => apiClient.get('/admin/audit', { params }),
  purgeAuditLog: () => apiClient.post('/admin/audit/purge'),
  // Regions
  getRegions: () => apiClient.get('/admin/regions'),
  getAllRegions: () => apiClient.get('/admin/regions/all'),
  createRegion: (data) => apiClient.post('/admin/regions', data),
  updateRegion: (id, data) => apiClient.put(`/admin/regions/${id}`, data),
  deleteRegion: (id) => apiClient.delete(`/admin/regions/${id}`),
  // Dropdown options (Payment Methods, Billing Frequencies, etc.)
  getServiceDescription: () => apiClient.get('/admin/service-description'),
  saveServiceDescription: (data) => apiClient.put('/admin/service-description', data),
  getAppServerPurposes: () => apiClient.get('/admin/app-server-purposes'),
  saveAppServerPurposes: (purposes) => apiClient.put('/admin/app-server-purposes', { purposes }),
  getCountries: () => apiClient.get('/admin/countries'),
  saveCountries: (countries) => apiClient.put('/admin/countries', { countries }),
  getProductInclusions: (product) => apiClient.get('/admin/product-inclusions', { params: product ? { product } : {} }),
  createProductInclusion: (data) => apiClient.post('/admin/product-inclusions', data),
  updateProductInclusion: (id, data) => apiClient.put(`/admin/product-inclusions/${id}`, data),
  deleteProductInclusion: (id) => apiClient.delete(`/admin/product-inclusions/${id}`),
  getAddonUserPricing: () => apiClient.get('/admin/pricing/addon-users'),
  updateAddonUserPricing: (configType, data) => apiClient.put(`/admin/pricing/addon-users/${configType}`, data),
  getAllDropdownOptions: (category) => apiClient.get('/admin/dropdown-options/all', { params: category ? { category } : {} }),
  getDropdownOptions: (category) => apiClient.get('/admin/dropdown-options', { params: { category } }),
  createDropdownOption: (data) => apiClient.post('/admin/dropdown-options', data),
  updateDropdownOption: (id, data) => apiClient.put(`/admin/dropdown-options/${id}`, data),
  deleteDropdownOption: (id) => apiClient.delete(`/admin/dropdown-options/${id}`),
  // SMTP
  testSmtp: () => apiClient.post('/admin/smtp/test'),
  // Signing email toggle
  getSigningEmailEnabled: () => apiClient.get('/admin/signing-email-enabled'),
  setSigningEmailEnabled: (enabled) => apiClient.put('/admin/signing-email-enabled', { enabled }),
  // Item codes
  getItemCodes: () => apiClient.get('/admin/item-codes'),
  saveItemCodes: (data) => apiClient.put('/admin/item-codes', data),
  // FX rates
  getFxRates: () => apiClient.get('/admin/fx-rates'),
  refreshFxRates: () => apiClient.post('/admin/fx-rates/refresh'),
  // GDPR exports
  exportCustomerData: (id) => apiClient.get(`/gdpr/export/customer/${id}`, { responseType: 'blob' }),
  exportTenantData: (id) => apiClient.get(`/gdpr/export/tenant/${id}`, { responseType: 'blob' }),
};
